import React, { useState, useRef, useMemo } from "react";
import "@fullcalendar/daygrid/main.css";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import GeneralCalendar from "src/components/GeneralComponents/GeneralCalendar";
import {
  programmeDetailApi,
  programme_workout_list_api,
} from "src/DAL/programmeWorkout/programmeWorkout";
import { memberDetail, memberRewardDetail } from "src/DAL/Profile/Profile";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const get_view_dates = (calendarValue) => {
  let tool = calendarValue;

  const startOfMonth = moment().startOf(tool);
  const daysToSubtract = startOfMonth.day() - 0;
  const previousSunday = startOfMonth.subtract(daysToSubtract, "days");
  let previousSundayDate = previousSunday.toDate();
  let add_days = 42;
  if (tool == "week") {
    add_days = 6;
  } else if (tool == "day") {
    add_days = 0;
  }
  let nextSaturday = startOfMonth.add(add_days, "days");
  let nextSaturdayDate = nextSaturday.toDate();
  if (tool == "day") {
    previousSundayDate = new Date();
    nextSaturdayDate = new Date();
  }

  return {
    start_date: previousSundayDate,
    end_date: nextSaturdayDate,
  };
};

const getEndDateForRequest = (
  requestedEndDate,
  isWorkoutVisiblePeriodEnabled,
  workoutVisibleTimePeriod,
) => {
  if (!isWorkoutVisiblePeriodEnabled) {
    return requestedEndDate;
  }

  const calculatedVisibleEndDate = moment()
    .add(workoutVisibleTimePeriod - 1, "days")
    .format("YYYY-MM-DD");

  return moment(requestedEndDate).isAfter(calculatedVisibleEndDate)
    ? calculatedVisibleEndDate
    : requestedEndDate;
};

function ProgramCalender() {
  const calendarRef = useRef(null);
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [submitted, setSubmitted] = useState(false);
  const [currentDate, setCurrentDate] = useState(get_view_dates("month"));
  const programmeId = state?._id?._id || state?.program_info?.program_id;

  const startDateStr = useMemo(
    () => moment(currentDate.start_date).format("YYYY-MM-DD"),
    [currentDate.start_date],
  );

  const endDateStr = useMemo(
    () => moment(currentDate.end_date).format("YYYY-MM-DD"),
    [currentDate.end_date],
  );

  const processWorkoutResponse = (result) => {
    const mergedData = [
      ...result?.workouts,
      ...result?.program_workouts.filter(
        (item) =>
          !result?.workouts.find(
            (existingItem) => existingItem.workout_id === item._id,
          ),
      ),
    ];

    const processedData = mergedData.map((workout) => ({
      ...workout,
      exercises: workout.exercises?.map((exercise) => ({
        ...exercise,
        sets: exercise.sets?.map((set) => ({
          ...set,
          vdot: null,
          pace_low: 0,
          pace_up: 423,
        })),
        sub_exercises: exercise.sub_exercises?.map((subExercise) => ({
          ...subExercise,
          sets: subExercise.sets?.map((set) => ({
            ...set,
            vdot: null,
            pace_low: 0,
            pace_up: 423,
          })),
        })),
      })),
    }));

    return {
      workouts: processedData,
      reordered_workouts: result?.reordered_workouts || [],
      levelStats: result?.workout_stats || {},
      timeZone: "asia/karachi",
    };
  };

  const goToToday = () => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      const viewStart = calendarApi.view.currentStart;
      const viewMonth = viewStart.getMonth();
      const viewYear = viewStart.getFullYear();

      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();

      const isSameMonth = viewMonth === todayMonth && viewYear === todayYear;

      if (!isSameMonth) {
        calendarApi.gotoDate(today);
        const newDateRange = {
          start_date: calendarApi.view.activeStart,
          end_date: calendarApi.view.activeEnd,
        };
        setCurrentDate(newDateRange);
      } else {
        const todayDate = today.toISOString().slice(0, 10);
        const todayCell = document.querySelector(
          `.fc-day[data-date="${todayDate}"]`,
        );
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  const prefetchAdjacentMonths = async () => {
    if (!calendarRef?.current || !programmeId) return;

    const calendarApi = calendarRef.current.getApi();
    const originalDate = calendarApi.getDate();

    calendarApi.next();
    const nextView = calendarApi.view;
    const nextRange = {
      start_date: nextView.activeStart,
      end_date: nextView.activeEnd,
    };

    calendarApi.gotoDate(originalDate);

    calendarApi.prev();
    const prevView = calendarApi.view;
    const prevRange = {
      start_date: prevView.activeStart,
      end_date: prevView.activeEnd,
    };

    calendarApi.gotoDate(originalDate);

    const buildPostData = (range) => {
      const rangeEndDate = moment(range.end_date).format("YYYY-MM-DD");

      return {
        start_date: moment(range.start_date).format("YYYY-MM-DD"),
        end_date: getEndDateForRequest(
          rangeEndDate,
          isWorkoutVisiblePeriodEnabled,
          workoutVisibleTimePeriod,
        ),
        slug: programmeId,
      };
    };

    const nextPostData = buildPostData(nextRange);
    const prevPostData = buildPostData(prevRange);

    try {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: [
            "programmeWorkoutList",
            programmeId,
            moment(nextRange.start_date).format("YYYY-MM-DD"),
            moment(nextRange.end_date).format("YYYY-MM-DD"),
            false,
            workoutVisibleTimePeriod ?? "",
          ],
          queryFn: async () => {
            const result = await programme_workout_list_api(nextPostData);
            if (result?.code === 200) {
              return processWorkoutResponse(result);
            }
            return {};
          },
        }),
        queryClient.prefetchQuery({
          queryKey: [
            "programmeWorkoutList",
            programmeId,
            moment(prevRange.start_date).format("YYYY-MM-DD"),
            moment(prevRange.end_date).format("YYYY-MM-DD"),
            false,
            workoutVisibleTimePeriod ?? "",
          ],
          queryFn: async () => {
            const result = await programme_workout_list_api(prevPostData);
            if (result?.code === 200) {
              return processWorkoutResponse(result);
            }
            return {};
          },
        }),
      ]);
    } catch (error) {
      console.log("Prefetch error:", error);
    }
  };

  const { data: memberData = {}, isLoading: isMemberLoading } = useQuery({
    queryKey: ["memberDetail"],
    queryFn: async () => {
      const result = await memberDetail();
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result.member;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const { data: memberRewardData = {}, isLoading: isRewardLoading } = useQuery({
    queryKey: ["memberRewardDetail"],
    queryFn: async () => {
      const result = await memberRewardDetail();
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result?.reward_points;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const programmeDetailQuery = useQuery({
    queryKey: ["programmeDetailApi", programmeId],
    enabled: Boolean(programmeId),
    queryFn: async () => {
      const result = await programmeDetailApi(programmeId);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result?.program;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const workoutVisibleTimePeriod =
    programmeDetailQuery.data?.workout_visible_time_period;

  const isWorkoutVisiblePeriodEnabled =
    workoutVisibleTimePeriod !== null &&
    workoutVisibleTimePeriod !== undefined &&
    workoutVisibleTimePeriod >= 0;

  const workoutListQuery = useQuery({
    queryKey: [
      "programmeWorkoutList",
      programmeId,
      startDateStr,
      endDateStr,
      submitted,
      workoutVisibleTimePeriod ?? "",
    ],
    keepPreviousData: true,
    queryFn: async () => {
      const postData = {
        start_date: startDateStr,
        end_date: getEndDateForRequest(
          endDateStr,
          isWorkoutVisiblePeriodEnabled,
          workoutVisibleTimePeriod,
        ),
        slug: programmeId,
      };

      const result = await programme_workout_list_api(postData);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }

      return processWorkoutResponse(result);
    },
    onSuccess: () => {
      setTimeout(() => {
        prefetchAdjacentMonths();
      }, 100);
      setSubmitted(false);
      const today = new Date();
      const todayDate = today.toISOString().slice(0, 10);
      const todayCell = document.querySelector(
        `.fc-day[data-date="${todayDate}"]`,
      );
      if (todayCell) {
        todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const isLoading = workoutListQuery?.isLoading && !workoutListQuery?.data;

  const manualLoading =
    workoutListQuery?.isFetching && !workoutListQuery?.isLoading;

  const programWorkoutList = useMemo(
    () => workoutListQuery.data?.workouts || [],
    [workoutListQuery.data?.workouts],
  );

  const reorderedWorkoutList = useMemo(
    () => workoutListQuery.data?.reordered_workouts || [],
    [workoutListQuery.data?.reordered_workouts],
  );

  const levelStats = useMemo(
    () => workoutListQuery.data?.levelStats || {},
    [workoutListQuery.data?.levelStats],
  );

  const defaultTimeZone = workoutListQuery.data?.timeZone || "";

  const reloadList = () => {
    programmeDetailQuery.refetch();
    workoutListQuery.refetch();
  };

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <GeneralCalendar
        programWorkoutList={programWorkoutList}
        reorderedWorkoutList={reorderedWorkoutList}
        setCurrentDate={setCurrentDate}
        showAddEvent={true}
        time_zone={defaultTimeZone}
        currentDate={currentDate}
        isLoading={isLoading}
        reloadList={reloadList}
        calendar_title={state?.program_info?.title || state?.title}
        programme={state}
        member_vdot={
          memberData?.pace_calculator ? memberData?.vdot?.vdot_value : null
        }
        memberRewardData={memberRewardData}
        setSubmitted={setSubmitted}
        submitted={submitted}
        calendarRef={calendarRef}
        goToToday={goToToday}
        levelStats={levelStats}
        manualLoading={manualLoading}
      />
    </>
  );
}

export default ProgramCalender;
